import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import {
  LocalOffer,
  Storefront,
  Bolt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: 'background.default', overflow: 'hidden' }}>
      {/* Hero - FIXED Working Shapes */}
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          background: `
            linear-gradient(135deg, #0f5328 0%, #1a7f3a 25%, #2d9e4a 50%, #4caf50 75%, #66bb6a 100%),
            radial-gradient(circle at 15% 25%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 85% 65%, rgba(255,255,255,0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 35% 15%, rgba(76,175,80,0.3) 0%, transparent 60%),
            radial-gradient(circle at 65% 85%, rgba(26,127,58,0.25) 0%, transparent 50%)
          `,
          backgroundSize: '400% 400%, 250px 250px, 350px 350px, 300px 200px, 220px 220px',
          animation: 'heroShift 6s ease infinite',
        }}
      >
        {/* SHAPE 1: Large Green Circle */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            background: 'radial-gradient(circle, rgba(76,175,80,0.5) 0%, rgba(26,127,58,0.2) 70%)',
            borderRadius: '50%',
            animation: 'float1 18s infinite linear',
            boxShadow: '0 0 40px rgba(76,175,80,0.6)',
            zIndex: 1,
          }}
        />
        
        {/* SHAPE 2: Large White Circle */}
        <Box
          sx={{
            position: 'absolute',
            top: '65%',
            right: '8%',
            width: 160,
            height: 160,
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 70%)',
            borderRadius: '50%',
            animation: 'float2 14s infinite linear',
            boxShadow: '0 0 30px rgba(255,255,255,0.5)',
            zIndex: 1,
          }}
        />
        
        {/* SHAPE 3: Green Triangle */}
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            right: '15%',
            width: 140,
            height: 140,
            background: 'linear-gradient(45deg, rgba(46,158,74,0.45) 0%, rgba(102,187,106,0.3) 100%)',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            animation: 'float3 16s infinite linear',
            boxShadow: '0 0 25px rgba(46,158,74,0.5)',
            zIndex: 1,
          }}
        />
        
        {/* SHAPE 4: Bright Green Circle */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '20%',
            width: 100,
            height: 100,
            background: 'radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(76,175,80,0.4) 70%)',
            borderRadius: '50%',
            animation: 'float4 12s infinite linear',
            boxShadow: '0 0 20px rgba(76,175,80,0.6)',
            zIndex: 1,
          }}
        />
        
        {/* SHAPE 5: Green Oval */}
        <Box
          sx={{
            position: 'absolute',
            top: '45%',
            right: '25%',
            width: 180,
            height: 80,
            background: 'linear-gradient(90deg, rgba(26,127,58,0.4) 0%, rgba(46,158,74,0.5) 50%, rgba(102,187,106,0.3) 100%)',
            borderRadius: '50px',
            animation: 'float5 22s infinite linear',
            boxShadow: '0 0 35px rgba(26,127,58,0.5)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Grid container alignItems="center" spacing={6}>
            <Grid item xs={12} md={6}>
              <Chip
                icon={<Bolt sx={{ fontSize: 20 }} />}
                label="LOCAL SAVINGS NETWORK"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  backdropFilter: 'blur(15px)',
                  color: 'white',
                  fontWeight: 700,
                  mb: 3,
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                fontWeight={800}
                sx={{
                  fontSize: { xs: '3rem', md: '4.5rem' },
                  lineHeight: 1.1,
                  mb: 3,
                  background: 'linear-gradient(45deg, #fff 0%, rgba(255,255,255,0.9) 50%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}
              >
                SeekDeals
              </Typography>
              <Typography
                variant="h4"
                fontWeight={300}
                sx={{
                  mb: 4,
                  color: 'rgba(255,255,255,0.98)',
                  lineHeight: 1.4,
                  maxWidth: 400,
                  backdropFilter: 'blur(10px)',
                }}
              >
                Unbeatable local deals from trusted vendors in your city
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LocalOffer />}
                  onClick={() => navigate('/deals')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.98)',
                    color: '#1a7f3a',
                    fontWeight: 700,
                    px: 5,
                    py: 1.5,
                    borderRadius: 50,
                    fontSize: '1.1rem',
                    boxShadow: '0 12px 40px rgba(255,255,255,0.4)',
                    backdropFilter: 'blur(25px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                      bgcolor: 'white',
                      transform: 'translateY(-6px)',
                      boxShadow: '0 24px 60px rgba(255,255,255,0.5)',
                    },
                  }}
                >
                  Claim Deals Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Storefront />}
                  onClick={() => navigate('/vendors')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.6)',
                    color: 'white',
                    backdropFilter: 'blur(20px)',
                    px: 5,
                    py: 1.5,
                    borderRadius: 50,
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.9)',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(255,255,255,0.3)',
                    },
                  }}
                >
                  Explore Vendors
                </Button>
              </Stack>
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(30px)',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.25)',
                  height: 'fit-content',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}
              >
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: 'white' }}>
                  By the Numbers
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Typography variant="h2" fontWeight={900} color="white" sx={{ lineHeight: 1, mb: 1 }}>
                      10K+
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.9)" fontWeight={500}>
                      Active Deals
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h2" fontWeight={900} color="white" sx={{ lineHeight: 1, mb: 1 }}>
                      500+
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.9)" fontWeight={500}>
                      Local Vendors
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h2" fontWeight={900} color="white" sx={{ lineHeight: 1, mb: 1 }}>
                      50K+
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.9)" fontWeight={500}>
                      Happy Users
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h2" fontWeight={900} color="white" sx={{ lineHeight: 1, mb: 1 }}>
                      20+
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.9)" fontWeight={500}>
                      Cities Covered
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid> */}
          </Grid>
        </Container>
      </Box>
      
      {/* WORKING SHAPE ANIMATIONS */}
      <style jsx>{`
        @keyframes heroShift {
          0%, 100% { background-position: 0% 50%, 20% 30%, 85% 65%, 35% 15%, 65% 85%; }
          25% { background-position: 100% 50%, 100% 100%, 15% 100%, 85% 50%, 35% 0%; }
          50% { background-position: 50% 100%, 0% 0%, 100% 100%, 0% 50%, 100% 50%; }
          75% { background-position: 0% 50%, 0% 100%, 85% 0%, 35% 100%, 65% 50%; }
        }
        
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          25% { transform: translate(40px, -40px) rotate(90deg) scale(1.1); }
          50% { transform: translate(-20px, 30px) rotate(180deg) scale(0.95); }
          75% { transform: translate(25px, -25px) rotate(270deg) scale(1.05); }
        }
        
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-50px, 40px) rotate(-120deg); }
          66% { transform: translate(35px, -35px) rotate(-240deg); }
        }
        
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          33% { transform: translate(-30px, 25px) rotate(120deg) scale(1.15); }
          66% { transform: translate(20px, -20px) rotate(240deg) scale(0.9); }
        }
        
        @keyframes float4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-25px, 25px) rotate(180deg); }
        }
        
        @keyframes float5 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.1); }
          66% { transform: translate(-25px, 20px) scale(0.95); }
        }
      `}</style>
    </Box>
  );
};

export default About;
